import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useRequest } from "../hooks/useRequest"
import { StartPopupData } from "../types/entities/startPopup"
import Loader from "./Loader"
import SwipeSheetPanel from "./SwipeSheetPanel"
import { markStartPopupShown, wasStartPopupShownToday } from "../lib/startPopupStorage"

const POPUP_SHEET_CLASS =
    "relative flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-[28px] md:h-auto md:max-h-[min(92svh,820px)] md:w-full md:max-w-[428px] md:rounded-[36px]"

type StartPopupProps = {
    cinemaId: number
}

export default function StartPopup({ cinemaId }: StartPopupProps) {
    const { requestData } = useRequest()
    const [data, setData] = useState<StartPopupData | null>(null)
    const [checked, setChecked] = useState(false)
    const [imageLoaded, setImageLoaded] = useState(false)

    useEffect(() => {
        const fetchPopup = async () => {
            if (wasStartPopupShownToday(cinemaId)) {
                setData(null)
                return
            }

            const res = await requestData<StartPopupData>({
                type: "GET",
                path: `cinema/${cinemaId}/startpopup`,
                disableError: true
            })

            if (res.status === "success" && res.data) {
                if (wasStartPopupShownToday(cinemaId)) {
                    setData(null)
                    return
                }
                setData(res.data)
                setChecked(false)
                setImageLoaded(false)
            } else {
                setData(null)
            }
        }

        fetchPopup()
    }, [cinemaId])

    if (!data) return null

    const disabled = Boolean(data.agree) && !checked

    const close = () => {
        markStartPopupShown(cinemaId)
        setData(null)
    }

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-end justify-center overflow-hidden md:items-center md:px-4"
            style={{ background: "rgba(10,18,42,0.8)", backdropFilter: "blur(6px)" }}
            onMouseDown={e => {
                if (e.target === e.currentTarget) close()
            }}>
            <SwipeSheetPanel
                onDismiss={close}
                className={POPUP_SHEET_CLASS}
                style={{
                    background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                    border: "1.5px solid rgba(255,255,255,0.12)",
                }}
                motionProps={{
                    initial: { y: "100%", opacity: 0 },
                    animate: { y: 0, opacity: 1 },
                    exit: { y: "100%", opacity: 0 },
                    transition: { type: "spring", stiffness: 320, damping: 32 },
                    onMouseDown: e => e.stopPropagation(),
                }}>
                <div className="flex min-h-0 w-full flex-1 flex-col gap-5 overflow-hidden px-5 pb-[max(1.25rem,var(--safe-bottom))] pt-1 md:gap-8 md:px-10 md:py-10">
                    <div className="flex w-full flex-col items-center gap-4">
                        <h2
                            className="w-full text-center text-[22px] leading-tight font-bold text-white md:text-[26px]"
                            style={{ fontFamily: "'Tilda Sans', sans-serif", letterSpacing: "-0.02em" }}>
                            {data.title}
                        </h2>
                        <p
                            className="w-full text-center text-[15px] leading-relaxed text-white/70"
                            style={{ fontFamily: "'Manrope', sans-serif" }}>
                            {data.body ?? ""}
                        </p>
                        {data.images?.[0] && (
                            <div className="relative w-full">
                                <img
                                    src={data.images[0]}
                                    alt=""
                                    className={`w-full rounded-[18px] object-cover md:h-[200px] transition-opacity ${imageLoaded ? "opacity-100" : "opacity-0"}`}
                                    onLoad={() => setImageLoaded(true)}
                                />
                                {!imageLoaded && (
                                    <div className="absolute inset-0 flex min-h-[120px] items-center justify-center md:min-h-[200px]">
                                        <Loader />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {data.agree && (
                        <label className="flex w-full cursor-pointer items-center gap-3">
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={e => setChecked(e.target.checked)}
                                className="h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-white/30 shadow transition-all checked:bg-primary hover:border-white/50"
                            />
                            <span className="text-[13px] text-white/80" style={{ fontFamily: "'Manrope', sans-serif" }}>
                                Принимаю
                            </span>
                        </label>
                    )}

                    <button
                        type="button"
                        onClick={close}
                        disabled={disabled}
                        className="flex h-12 w-full shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary px-8 text-base font-bold text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            boxShadow: disabled ? "none" : "0 0 20px rgba(228,32,107,0.4)",
                        }}>
                        {data.button}
                    </button>
                </div>
            </SwipeSheetPanel>
        </div>,
        document.body,
    )
}
